import React, { useMemo } from 'react';
import { Badge } from '@roger/ui-components';
import type { PayerResponseReason } from '../../../services/api/types.gen';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import {
  formatReason,
  groupReasonsByServiceLine,
  serviceLineLabel,
  sourceLabel,
} from './payerReasonLabels';

type ReasonKind = 'claim' | 'noa';

const ReasonItems: React.FC<{
  reasons: PayerResponseReason[];
  payerClaimNumber?: string | null;
  kind: ReasonKind;
}> = ({ reasons, payerClaimNumber, kind }) => (
  <ol className='space-y-3'>
    {reasons.map((reason, index) => {
      const { text, hint } = formatReason(reason, payerClaimNumber, kind);
      return (
        <li key={index} className='min-w-0 space-y-1'>
          <p className='text-sm text-foreground'>{text}</p>
          {hint ? (
            <p className='text-xs text-muted-foreground'>{hint}</p>
          ) : null}
          <div className='flex flex-wrap items-center gap-2'>
            <Badge variant='secondary' className='whitespace-nowrap'>
              {sourceLabel(reason.source)}
            </Badge>
            {reason.received_on ? (
              <span className='text-xs text-muted-foreground'>
                {formatDateForDisplay(reason.received_on)}
              </span>
            ) : null}
          </div>
        </li>
      );
    })}
  </ol>
);

/**
 * The ordered reasons the payer (or Stedi, before the payer) gave. One list for
 * the drawer and for the claim worksheet, so a claim and an NOA read the same.
 *
 * Reasons the 835 reported inside a service line sit under that line; the rest
 * stay together as the whole claim's. With no line-level reason the
 * list is the single flat one it always was.
 */
export const PayerReasonList: React.FC<{
  reasons: PayerResponseReason[];
  payerClaimNumber?: string | null;
  /** Names the thing the payer refused, for the no-reason fallback line. */
  kind?: ReasonKind;
}> = ({ reasons, payerClaimNumber, kind = 'claim' }) => {
  // The worksheet editor re-renders on every keystroke; the reasons do not.
  const { claimLevel, lines } = useMemo(
    () => groupReasonsByServiceLine(reasons),
    [reasons]
  );
  const lineLabels = useMemo(
    () => lines.map(({ line }) => serviceLineLabel(line)),
    [lines]
  );

  if (reasons.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>No reason received yet.</p>
    );
  }

  if (lines.length === 0) {
    return (
      <ReasonItems
        reasons={reasons}
        payerClaimNumber={payerClaimNumber}
        kind={kind}
      />
    );
  }

  return (
    <div className='space-y-4'>
      {claimLevel.length > 0 ? (
        <section
          className='min-w-0 space-y-2'
          data-testid='payer-reasons-claim-level'
        >
          <p className='text-sm font-semibold text-foreground'>Whole claim</p>
          <ReasonItems
            reasons={claimLevel}
            payerClaimNumber={payerClaimNumber}
            kind={kind}
          />
        </section>
      ) : null}
      {lines.map(({ line, reasons: lineReasons }, index) => (
        <section
          key={line.id}
          className='min-w-0 space-y-2 border-t border-border/60 pt-3 first:border-t-0 first:pt-0'
          data-testid='payer-reasons-service-line'
        >
          <p className='text-sm font-semibold text-foreground'>
            {lineLabels[index]}
          </p>
          <ReasonItems
            reasons={lineReasons}
            payerClaimNumber={payerClaimNumber}
            kind={kind}
          />
        </section>
      ))}
    </div>
  );
};

export default PayerReasonList;
